import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterSelection, FilterFrame } from '../Filters.style';
import { FilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import useAnimations from '../../../hooks/Animations/useAnimations';
import RadioButtonSelector from '../../RadioButtonSelector';
import FilterExposedHeader from '../FilterExposedHeader';

interface VehicleTypeSelectorTier3Types {
  filterType: string;
  usedInventoryTypeOptions: {
    [key: string]: FilterOption;
  };
  newInventoryTypeOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
  isVehiclesLoading: boolean;
  openedFiltersList: ExposedFiltersTypes;
}

const VehicleTypeSelectorTier3: React.FC<VehicleTypeSelectorTier3Types> = ({
  filterType,
  usedInventoryTypeOptions,
  newInventoryTypeOptions,
  confirmedFilters,
  isVehiclesLoading,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const selectedOption = useMemo(() => {
    const selectedVehicleType = confirmedFilters.vehicleType;
    return selectedVehicleType;
  }, [confirmedFilters]);

  const showFilter = openedFiltersList[filterType];
  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  const selectedUsedInventoryType = useMemo(() => {
    const selectedVehicleType = confirmedFilters.usedInventoryType;
    return selectedVehicleType;
  }, [confirmedFilters.usedInventoryType]);

  const selectedNewInventoryType = useMemo(() => {
    const selectedVehicleType = confirmedFilters.availableInMonths;
    return selectedVehicleType;
  }, [confirmedFilters]);

  const handleChangeUsedFilterValue = (isNonAudiFilter: boolean | undefined, newValue: string) => {
    if (isNonAudiFilter) {
      handleAddFilter('nonAudi', newValue);
      return;
    }
    handleAddFilter('usedInventoryType', newValue);
  };

  // TO FIX LATER
  // Track the previous confirmed vehicleType using useRef (persists across renders)
  const prevVehicleTypeRef = useRef(confirmedFilters.vehicleType);
  // Determine if vehicle type has changed since last render
  const didVehicleTypeChange = prevVehicleTypeRef.current !== confirmedFilters.vehicleType;
  // Update ref after comparison is made — ensures we only detect the change once
  useEffect(() => {
    if (didVehicleTypeChange) {
      prevVehicleTypeRef.current = confirmedFilters.vehicleType;
    }
  }, [confirmedFilters.vehicleType, didVehicleTypeChange]);
  // Final computed loading flag — only true if loading AND vehicle type has changed
  const showLoadingForUsed = isVehiclesLoading && didVehicleTypeChange;

  const selectedFilterLabel = useMemo(() => {
    if (selectedOption === 'new')
      return newInventoryTypeOptions[`availableInMonths${selectedNewInventoryType}`].label;
    if (selectedOption === 'used') {
      const cleanValue = selectedUsedInventoryType.replace('has-warranties.', '');
      return usedInventoryTypeOptions[cleanValue].label;
    }
    return '';
  }, [
    newInventoryTypeOptions,
    selectedNewInventoryType,
    selectedOption,
    selectedUsedInventoryType,
    usedInventoryTypeOptions,
  ]);

  return (
    <Filter data-cy="pre-owned-vehicle-type-selector">
      <FilterExposedHeader
        filterType={filterType}
        vehicleType={confirmedFilters.vehicleType}
        selectedFilterLabels={[selectedFilterLabel?.toString() ?? '']}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      <>
        {dropdownAnimation(
          (props, item) =>
            item && (
              <FilterSelection style={props}>
                <FilterFrame style={filtersAccordianAnimation}>
                  {selectedOption === 'new' && (
                    <RadioButtonSelector
                      options={newInventoryTypeOptions}
                      value={selectedNewInventoryType.toString()}
                      onChange={(_isNonAudiFilter, newValue) => {
                        handleAddFilter('availableInMonths', newValue);
                      }}
                      title={t(`vlp.filters.filterOptions.newInventoryType.title`)}
                      name="new-inventory-type-options"
                    />
                  )}
                  {selectedOption === 'used' && (
                    <RadioButtonSelector
                      options={usedInventoryTypeOptions}
                      value={selectedUsedInventoryType}
                      isVehiclesLoading={showLoadingForUsed}
                      onChange={handleChangeUsedFilterValue}
                      title={t(`vlp.filters.filterOptions.vehicleType.preOwnedTitle`)}
                      name="used-inventory-type-options"
                    />
                  )}
                </FilterFrame>
              </FilterSelection>
            ),
        )}
      </>
    </Filter>
  );
};

export default VehicleTypeSelectorTier3;
